/** Tile size in world pixels. The space grid is expressed in tiles. */
export const TILE_SIZE = 32;
/** Default space canvas size, in tiles. */
export const DEFAULT_SPACE_COLS = 60;
export const DEFAULT_SPACE_ROWS = 40;
/** Avatar walking speed, in world pixels per second. */
export const AVATAR_SPEED = 190;
/** How often a client broadcasts its position while moving (ms). */
export const MOVE_BROADCAST_INTERVAL = 60;
/**
 * Spatial audio falloff, in world pixels.
 * Inside `AUDIO_FULL_RADIUS` a peer is heard at full volume; past
 * `AUDIO_MAX_RADIUS` they are silent. Between the two, volume falls off
 * smoothly. Mirrors the "walk up to someone to talk" behaviour of a
 * physical office.
 */
export const AUDIO_FULL_RADIUS = 90;
export const AUDIO_MAX_RADIUS = 300;
/** Distance at which two avatars are considered to be in conversation. */
export const PROXIMITY_RADIUS = AUDIO_MAX_RADIUS;
/**
 * The quietest a voice can be muffled to by walls before it is treated as
 * silence.
 *
 * Not zero on purpose: a wall between two people standing either side of it
 * should sound like a wall, not like an empty room. Below this the connection
 * is not worth holding open, so the mesh drops it.
 */
export const OCCLUSION_FLOOR = 0.05;
/**
 * How far an avatar can reach to work a prop, in world pixels — about three
 * tiles. Short on purpose: you should have to walk to the door to close it.
 */
export const REACH_RADIUS = TILE_SIZE * 3;
/**
 * Past this many people in a full mesh call, each participant uploads more
 * copies of their stream than a laptop on home wifi comfortably can. It is a
 * warning, not a cap: the signalling service leaves the room open and the
 * meeting UI points at the SFU-backed office space instead.
 */
export const MESH_SOFT_LIMIT = 6;
/**
 * Meeting recordings.
 *
 * Calls are a peer-to-peer mesh, so nothing on the server ever hears the
 * audio: the host's browser mixes every track it can hear into one stream and
 * records that. It ships the recording in slices rather than at the end, so a
 * crashed tab or a dropped laptop lid loses at most one slice.
 */
export const MEETING_RECORDING_STATUSES = [
    'recording',
    'processing',
    'ready',
    'failed',
    'unavailable',
];
export const MEETING_RECORDING_CHUNK_MS = 30_000;
export const MEETING_RECORDING_MIME = 'audio/webm;codecs=opus';
/** A slice of opus at speech bitrates is well under a megabyte; this is the brake. */
export const MEETING_RECORDING_MAX_CHUNK_BYTES = 8 * 1024 * 1024;
/** Twelve hours, which is longer than any meeting anyone should be in. */
export const MEETING_RECORDING_MAX_SECONDS = 12 * 60 * 60;
export const MAX_MESSAGE_LENGTH = 8000;
export const MAX_TASK_TITLE_LENGTH = 300;
export const MAX_REMINDER_TITLE_LENGTH = 200;
export const MAX_REMINDER_NOTE_LENGTH = 500;
/**
 * A whisper reaches only someone you are standing next to. Deliberately the
 * full-volume radius rather than the audible one: if a whisper carried as far
 * as speech it would just be a DM with extra steps.
 */
export const WHISPER_RADIUS = AUDIO_FULL_RADIUS;
export const MAX_WHISPER_LENGTH = 280;
/** How long a whisper stays on screen before it fades, in ms. */
export const WHISPER_TTL = 12_000;
/**
 * The longest single line of recognised speech that will be relayed as a
 * caption. Recognisers emit a result per pause, so this is generous for a
 * sentence and still a bound on what one open microphone can send.
 */
export const MAX_CAPTION_LENGTH = 400;
/**
 * How long a caption stays on screen, in ms. Shorter than a whisper's: it is
 * a subtitle for something being said now, and a subtitle that outlives the
 * sentence stops matching the voice.
 */
export const CAPTION_TTL = 8_000;
/** How many lines the caption strip holds before the oldest drops off. */
export const MAX_VISIBLE_CAPTIONS = 3;
/**
 * How much a channel is allowed to interrupt someone.
 *
 * `mentions` is the useful middle: the channel still counts as unread, but
 * only a direct mention produces a notification.
 */
export const NOTIFICATION_LEVELS = ['all', 'mentions', 'none'];
/** How many earlier versions of a message are kept. */
export const MAX_MESSAGE_EDITS = 20;
/**
 * Largest conversation that still shows read receipts.
 *
 * A receipt answers "did the person I am talking to read this", which is a
 * question a DM or a small group actually has. Past a dozen people it stops
 * being an answer and becomes surveillance — "seen by 40 people" tells the
 * author nothing they can act on while telling everyone else exactly when
 * each of them opened the room. So the marker simply stops existing there
 * rather than being rendered smaller.
 */
export const RECEIPTS_MAX_MEMBERS = 12;
/** A quote is a pointer, not a copy — this is how much of it travels. */
export const QUOTE_EXCERPT_LENGTH = 220;
/** Furthest ahead a message can be scheduled. */
export const MAX_SCHEDULE_DAYS = 90;
/* ------------------------------- stories -------------------------------- */
/**
 * The one-tap reactions offered under a story.
 *
 * A short row rather than the full picker: a story is watched for five seconds,
 * and anything that takes a search box to answer will not be answered. The
 * first is what a double-tap sends.
 */
export const STORY_QUICK_REACTIONS = ['❤️', '🔥', '👏', '😂', '😮', '🙌'];
/** A reply to a story is a DM, so it is capped like the messages around it. */
export const MAX_STORY_REPLY_LENGTH = 1000;
/** How much of a story's caption travels on the reply card in the DM. */
export const STORY_REPLY_CAPTION_LENGTH = 120;
/* -------------------------------- polls -------------------------------- */
export const POLL_MAX_OPTIONS = 10;
export const POLL_MAX_OPTION_LENGTH = 120;
export const POLL_MAX_QUESTION_LENGTH = 300;
/* ---------------------------- pins and emoji ---------------------------- */
/** Past this a pinned list stops being a summary and becomes a second inbox. */
export const MAX_PINNED_MESSAGES = 50;
/** Custom emoji and stickers are org-scoped; these cap what one org can hold. */
export const MAX_CUSTOM_EMOJI = 500;
export const MAX_EMOJI_NAME_LENGTH = 32;
export const MAX_STICKER_PACKS = 50;
/**
 * Unicode skin-tone modifiers, in the order the picker shows them. `default`
 * carries no modifier — the yellow that means "a person", not "a pale person".
 */
export const SKIN_TONES = [
    'default',
    'light',
    'medium_light',
    'medium',
    'medium_dark',
    'dark',
];
export const SKIN_TONE_MODIFIERS = {
    default: '',
    light: '\u{1F3FB}',
    medium_light: '\u{1F3FC}',
    medium: '\u{1F3FD}',
    medium_dark: '\u{1F3FE}',
    dark: '\u{1F3FF}',
};
/**
 * Applies a skin tone to an emoji that supports one. The modifier goes after
 * the base character but before any zero-width-joiner sequence, so family and
 * profession emoji ("👩‍💻") tone their person rather than gaining a stray
 * swatch at the end.
 */
export function applySkinTone(emoji, tone) {
    const modifier = SKIN_TONE_MODIFIERS[tone];
    const stripped = emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
    if (!modifier)
        return stripped;
    const [base, ...rest] = stripped.split('‍');
    return [`${base}${modifier}`, ...rest].join('‍');
}
export const ROLES = ['owner', 'admin', 'member', 'guest'];
/**
 * What kind of account a user record is.
 *
 * An agent has a user document so that everything which references a person by
 * id — a meeting roster, a mention, the author of a note — keeps working
 * without a second code path. What it deliberately does not have is a
 * membership row: membership is what every member list, report and directory
 * query is built on, and an agent is not a member of the workspace. Its reach
 * comes from the `SpaceAgent` that drives it, nothing wider.
 */
export const USER_KINDS = ['human', 'agent'];
/**
 * A workspace is not created the moment somebody asks for one — a platform
 * admin reviews the request first. These are the states that review moves
 * through; `approved` is the only one with an organization behind it.
 */
export const WORKSPACE_REQUEST_STATUSES = ['pending', 'approved', 'rejected'];
export const TASK_STATUSES = ['backlog', 'todo', 'in_progress', 'in_review', 'done'];
export const TASK_PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'];
/**
 * What kind of work a task is — a bug is triaged differently from a chore
 * even when both sit in the same column. `task` is the unmarked default so
 * existing boards read exactly as they did before types existed.
 */
export const TASK_TYPES = ['task', 'bug', 'feature', 'improvement', 'chore'];
export const TASK_TYPE_INFO = {
    task: { label: 'Task', hint: 'A plain unit of work' },
    bug: { label: 'Bug', hint: 'Something is broken and needs fixing' },
    feature: { label: 'Feature', hint: 'New capability that does not exist yet' },
    improvement: { label: 'Improvement', hint: 'Existing behaviour, made better' },
    chore: { label: 'Chore', hint: 'Upkeep — refactors, upgrades, housekeeping' },
};
/**
 * A milestone is either still being worked toward or closed. There is no
 * "missed" state on purpose: a milestone past its date with open tasks is
 * simply late, and the progress numbers already say so.
 */
export const MILESTONE_STATUSES = ['open', 'closed'];
/**
 * The value shapes a project-defined custom field can take. `select` is the
 * only one with configuration (its options); the rest are just typed inputs.
 */
export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'select'];
/** Past this, per-project fields stop being metadata and become a database. */
export const MAX_PROJECT_CUSTOM_FIELDS = 20;
export const PRESENCE_STATUSES = ['online', 'busy', 'away', 'in_meeting', 'offline'];
/**
 * When pushes are held back on a schedule.
 *
 * `custom` is the start/end window someone typed in; `work_schedule` follows
 * the hours an admin set for them, so pushes stop the moment their day does
 * without a second copy of those hours to keep in step.
 */
export const QUIET_HOURS_MODES = ['off', 'custom', 'work_schedule'];
/** Why pushes are being held right now, when they are. */
export const DO_NOT_DISTURB_REASONS = ['dnd', 'quiet_hours', 'off_schedule'];
/**
 * "Indefinitely" is stored as a far-future `until` rather than a second flag,
 * so every reader asks one question — is it still before `until`? — and a
 * pause that was meant to be forever expires the same way a timed one does.
 */
export const DND_INDEFINITE_MINUTES = 60 * 24 * 365 * 10;
/**
 * The pause lengths offered everywhere a person can pause notifications.
 * `tomorrow` has no fixed length — see {@link dndPresetMinutes}.
 */
export const DND_PRESETS = [
    { key: '30m', label: '30 minutes', minutes: 30 },
    { key: '1h', label: '1 hour', minutes: 60 },
    { key: '2h', label: '2 hours', minutes: 120 },
    { key: 'tomorrow', label: 'Until tomorrow', minutes: null },
    { key: 'indefinite', label: 'Until I turn it off', minutes: DND_INDEFINITE_MINUTES },
];
/**
 * How long a preset pauses for, from `now`. "Until tomorrow" ends at 08:00
 * local time on the next day — the device's clock, since the person pressing
 * the button is looking at it.
 */
export function dndPresetMinutes(key, now = new Date()) {
    const preset = DND_PRESETS.find((item) => item.key === key);
    if (preset?.minutes)
        return preset.minutes;
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);
    return Math.max(1, Math.ceil((tomorrow.getTime() - now.getTime()) / 60_000));
}
/** Whether a stored `until` still holds back notifications. */
export function isDndActive(until, now = new Date()) {
    if (!until)
        return false;
    const at = new Date(until).getTime();
    return Number.isFinite(at) && at > now.getTime();
}
export const ZONE_KINDS = [
    'room',
    'class',
    'desk',
    'lounge',
    'focus',
    'stage',
    'broadcast',
    'queue',
    'portal',
];
export const CHANNEL_KINDS = ['channel', 'dm', 'room'];
/**
 * The assistant's name, wherever it speaks.
 *
 * One constant rather than a string typed into each client: it is now a name
 * people @mention in a channel, and a picker that offered "Itsappy AI" while
 * the account behind it was called something else would turn the typed name
 * into plain text that reached nobody.
 */
export const ASSISTANT_NAME = 'Itsappy AI';
/**
 * What Itsappy AI is allowed to propose doing, rather than only describing.
 *
 * Deliberately short. Every kind here needs a card a person can read at a
 * glance and a confirm button that runs it through the same API a hand would
 * have used — an action the model can propose but nobody can check is worse
 * than no action at all.
 */
export const ASSISTANT_ACTION_KINDS = [
    'create_task',
    'update_task',
    'post_message',
    'set_workspace_feature',
    'set_member_access',
];
/**
 * The kinds only a workspace owner may be offered.
 *
 * Administration is the one place where a wrong proposal is expensive — a
 * product switched off for everyone is felt by the whole workspace — so these
 * are held back at the source: a member is never told the protocol exists, and
 * an action of one of these kinds is dropped on the way back if the person
 * asking could not have run it by hand.
 */
export const ASSISTANT_ADMIN_ACTION_KINDS = [
    'set_workspace_feature',
    'set_member_access',
];
/**
 * What each kind of zone is for, in the words shown to the person standing in
 * it. Declared here so the editor's picker and the in-space panel cannot drift
 * apart, and so adding a kind fails to compile until it has been explained.
 */
export const ZONE_KIND_INFO = {
    room: {
        label: 'Room',
        hint: 'A meeting room. Audio stays inside when the room is private.',
    },
    class: {
        label: 'Classroom',
        hint: 'A teaching room. Whoever runs the workspace leads; everyone else is seated as a student.',
    },
    desk: {
        label: 'Desks',
        hint: 'A desk cluster — people here are usually heads-down.',
    },
    lounge: {
        label: 'Lounge',
        hint: 'Casual space. Anyone nearby can join the conversation.',
    },
    focus: {
        label: 'Focus pod',
        hint: 'A focus pod. Others cannot hear in, and you cannot hear out.',
    },
    stage: {
        label: 'Stage',
        hint: 'Presenting here carries to the whole zone regardless of distance.',
    },
    broadcast: {
        label: 'Broadcast',
        hint: 'Speak here and the whole floor hears you — through walls, at full volume. Private rooms stay sealed.',
    },
    queue: {
        label: 'Reception queue',
        hint: 'Wait here and someone will come for you. Whoever waited longest is called first.',
    },
    portal: {
        label: 'Portal',
        hint: 'Step in to travel — across the floor, or to another one.',
    },
};
/**
 * Starting layouts offered when creating a space. Declared here so the API,
 * the web creator and the mobile app all present the same catalogue.
 */
export const SPACE_TEMPLATES = [
    'startup_office',
    'corporate_office',
    'agency_studio',
    'coworking_hub',
    'engineering_floor',
    'classroom',
    'school',
    'clinic',
    'social_lounge',
    'support_center',
    'rooftop_garden',
    'media_studio',
    'wellness_floor',
    'campus',
    'conference',
    'legal_court',
    'egyptian_court',
    'judge_simulation',
    'blank',
];
export const SPACE_TEMPLATE_INFO = {
    startup_office: {
        label: 'Startup office',
        hint: 'Desks, focus pods, a meeting room and an all-hands stage',
    },
    corporate_office: {
        label: 'Corporate office',
        hint: 'A walled floor plan: reception, bench desks, boardroom, private offices and a break room',
    },
    agency_studio: {
        label: 'Agency studio',
        hint: 'Studio pods, a pitch room, a client lounge and a review wall',
    },
    coworking_hub: {
        label: 'Coworking hub',
        hint: 'Hot desks, phone booths, a cafe and two private suites',
    },
    engineering_floor: {
        label: 'Engineering floor',
        hint: 'Squad pods, a war room, pairing booths and a demo area',
    },
    classroom: {
        label: 'Classroom',
        hint: 'A lectern, seating rows and breakout tables for group work',
    },
    school: {
        label: 'School',
        hint: 'Four classrooms off a corridor, library and labs, the head’s office, school office, hall and canteen',
    },
    clinic: {
        label: 'Clinic',
        hint: 'Reception, a waiting room that calls people in turn, consult rooms and a treatment bay',
    },
    social_lounge: {
        label: 'Social lounge',
        hint: 'Fire pit, games corner, karaoke stage and quiet nooks',
    },
    support_center: {
        label: 'Support centre',
        hint: 'Support pods, an escalation war room and a coaching room',
    },
    rooftop_garden: {
        label: 'Rooftop garden',
        hint: 'Lawn, pond, picnic tables and a barbecue under the parasols',
    },
    media_studio: {
        label: 'Media studio',
        hint: 'Green screen, camera rigs, a sound booth and an edit bay',
    },
    wellness_floor: {
        label: 'Wellness floor',
        hint: 'Nap pods, a yoga studio, a small gym and a quiet library',
    },
    campus: {
        label: 'Campus',
        hint: 'Multiple buildings, a garden and an auditorium',
    },
    conference: {
        label: 'Conference',
        hint: 'Main stage, expo hall, breakout rooms and a networking lounge',
    },
    legal_court: {
        label: 'Legal court',
        hint: 'Courtroom, judge’s chambers, jury room, counsel rooms, filing office and public waiting',
    },
    egyptian_court: {
        label: 'Egyptian court',
        hint: 'One simple hall: a three-judge bench, the prosecution podium, the dock, counsel benches and public seating',
    },
    judge_simulation: {
        label: 'Judge simulation',
        hint: 'A mock courtroom for training judges, with an observation gallery, a control booth and a debrief room',
    },
    blank: {
        label: 'Blank floor',
        hint: 'An empty walled room to design from scratch',
    },
};
/* ------------------------------ industries ------------------------------ */
/**
 * What line of work a workspace is in.
 *
 * The workspace's industry is not decoration: it decides which starting
 * layouts its owner is offered, so a law firm is not asked whether it wants a
 * green screen and a school is not asked whether it wants a trading floor.
 * `general` is the unmarked default — every layout that suits any workplace is
 * offered to it, and nothing beyond that.
 */
export const WORKSPACE_INDUSTRIES = [
    'general',
    'technology',
    'education',
    'healthcare',
    'agency',
    'coworking',
    'media',
    'wellness',
    'support',
    'events',
    'legal',
];
export const WORKSPACE_INDUSTRY_INFO = {
    general: { label: 'General', hint: 'Any workplace — the layouts that suit everyone' },
    technology: { label: 'Technology', hint: 'Product and engineering teams' },
    education: { label: 'Education', hint: 'Schools, universities and training providers' },
    healthcare: { label: 'Healthcare', hint: 'Clinics, practices and care teams' },
    agency: { label: 'Agency', hint: 'Studios and client services' },
    coworking: { label: 'Coworking', hint: 'Shared floors and member desks' },
    media: { label: 'Media', hint: 'Production, broadcast and publishing' },
    wellness: { label: 'Wellness', hint: 'Fitness, therapy and wellbeing' },
    support: { label: 'Support', hint: 'Service desks and contact centres' },
    events: { label: 'Events', hint: 'Conferences, expos and community gatherings' },
    legal: { label: 'Legal', hint: 'Courts, chambers and legal practices' },
};
/**
 * Which industries each starting layout belongs to.
 *
 * An empty list means "any workplace": a blank floor, a lounge and a rooftop
 * are as useful to a clinic as to a games studio, and hiding them behind an
 * industry would only make the catalogue look broken. Everything else is
 * offered where it makes sense — a workspace owner picks from their own
 * industry, and a platform admin may reach past that for all of them.
 */
export const SPACE_TEMPLATE_INDUSTRIES = {
    // The two plain offices are everyone's: a clinic still has an admin floor,
    // and a school still has a staff room. They are also what a space defaults
    // to, so gating them would leave a workspace unable to build anything.
    startup_office: [],
    corporate_office: [],
    agency_studio: ['agency', 'media'],
    coworking_hub: ['coworking'],
    engineering_floor: ['technology'],
    classroom: ['education'],
    school: ['education'],
    clinic: ['healthcare', 'wellness'],
    social_lounge: [],
    support_center: ['support', 'technology'],
    rooftop_garden: [],
    media_studio: ['media', 'agency'],
    wellness_floor: ['wellness', 'healthcare'],
    campus: ['education'],
    conference: ['events'],
    legal_court: ['legal'],
    egyptian_court: ['legal'],
    // Mock hearings are how both a bench and a law faculty train, so the
    // simulation floor belongs to teaching as much as to the courts.
    judge_simulation: ['legal', 'education'],
    blank: [],
};
/**
 * The layouts a workspace in this industry may start a space from, in
 * catalogue order. Universal layouts are always included, so no industry ever
 * sees an empty picker.
 */
export function templatesForIndustry(industry) {
    return SPACE_TEMPLATES.filter((name) => {
        const industries = SPACE_TEMPLATE_INDUSTRIES[name];
        return industries.length === 0 || industries.includes(industry);
    });
}
/** Whether this layout is one that industry may start from. */
export function templateAllowedForIndustry(name, industry) {
    const industries = SPACE_TEMPLATE_INDUSTRIES[name];
    return industries.length === 0 || industries.includes(industry);
}
/** The layout a newly approved workspace's first floor is built from. */
export const INDUSTRY_DEFAULT_TEMPLATE = {
    general: 'startup_office',
    technology: 'startup_office',
    education: 'school',
    healthcare: 'clinic',
    agency: 'agency_studio',
    coworking: 'coworking_hub',
    media: 'media_studio',
    wellness: 'wellness_floor',
    support: 'support_center',
    events: 'conference',
    legal: 'legal_court',
};
/* ------------------------------ room tools ------------------------------ */
/**
 * What a room on a floor comes with.
 *
 * A room in this product is not a rectangle with a name — it is a place with
 * the same fittings every time: a board on the wall, a conversation that
 * outlives the people standing in it, a page of notes, a way to start a
 * meeting, and whatever the room keeps pinned up. Declaring the set here is
 * what stops one surface offering four of them and another offering two.
 */
export const ROOM_TOOLS = [
    'board',
    'chat',
    'notes',
    'meeting',
    'widgets',
    'games',
    'files',
];
export const ROOM_TOOL_INFO = {
    board: { label: 'Whiteboard', hint: "The board on this room's wall" },
    chat: { label: 'Room chat', hint: 'A conversation that stays with the room' },
    notes: { label: 'Notes', hint: 'A shared page for what was decided here' },
    meeting: { label: 'Meeting', hint: 'Start a call for the people in this room' },
    widgets: { label: 'Wall', hint: 'What this room keeps pinned up' },
    games: { label: 'Games', hint: 'Tables open in this room' },
    files: { label: 'Files', hint: 'The documents this room keeps' },
};
/**
 * The zone kinds that are rooms in the sense that matters here — somewhere
 * people gather with a door and a wall, rather than a walkway or a desk bank.
 * These are the zones wired with the full toolset.
 */
export const ROOM_ZONE_KINDS = [
    'room',
    'class',
    'focus',
    'stage',
    'lounge',
    'broadcast',
];
/** Whether this zone gets the room toolset. */
export function isRoomZone(kind) {
    return ROOM_ZONE_KINDS.includes(kind);
}
/* ------------------------------- bookings ------------------------------- */
/**
 * Booking a place on the floor for a while.
 *
 * Two kinds of resource and no more: a room, which is a zone, and a desk,
 * which is one prop. Both are things a hybrid team runs out of on a Tuesday,
 * and neither is a person — who sits where permanently is the space roster's
 * job, and a booking deliberately says nothing about it.
 */
export const BOOKING_KINDS = ['desk', 'room'];
/**
 * `released` is not a cancellation somebody made: it is the sweeper letting a
 * desk go after its booker never turned up, which reads differently on the
 * floor and in the inbox and so is worth its own word.
 */
export const BOOKING_STATUSES = ['booked', 'cancelled', 'released'];
/**
 * A lounge is somewhere you walk into, not somewhere you reserve, and a queue
 * or a portal is furniture of the building rather than a room. What is left is
 * the set of zones it makes sense to hold for an hour.
 */
export const BOOKABLE_ZONE_KINDS = [
    'room',
    'class',
    'focus',
    'stage',
    'broadcast',
];
/** Whether a room of this kind can be held for a while. */
export function isBookableZone(kind) {
    return BOOKABLE_ZONE_KINDS.includes(kind);
}
/** Nobody books a minute, and the grid people think in starts at a quarter. */
export const BOOKING_MIN_MINUTES = 15;
/**
 * The longest single booking. A day at a desk is the honest maximum: anything
 * longer is an assignment on the space roster, not a booking, and a week-long
 * "booking" is how a hot desk quietly stops being one.
 */
export const BOOKING_MAX_HOURS = 12;
/**
 * How far ahead a workspace lets people book by default. Overridden per
 * workspace by `settings.booking.maxDaysAhead`.
 */
export const BOOKING_DEFAULT_MAX_DAYS_AHEAD = 30;
/** How long before a booking starts its holder is nudged. */
export const BOOKING_REMINDER_MINUTES = 10;
/**
 * How long a desk waits for somebody who booked it. Twenty minutes is late
 * enough to be a no-show rather than a slow lift, and short enough that the
 * desk is still worth having for the rest of the morning.
 */
export const BOOKING_NO_SHOW_MINUTES = 20;
/* ----------------------------- coffee pairing --------------------------- */
/**
 * Where a suggested coffee ended up.
 *
 * `suggested` is the pairing as the sweeper drew it. `met` is the only happy
 * ending, and it is recorded either because the two of them stood in the same
 * room long enough or because one of them said so. `skipped` is somebody
 * declining this week's match — kept rather than deleted, because a pair that
 * was offered and turned down is still a pair the next draw should avoid
 * repeating. `released` is the pairing the sweeper itself withdrew.
 */
export const COFFEE_PAIRING_STATUSES = ['suggested', 'met', 'skipped', 'released'];
/**
 * The days a workspace pairs people on by default: Monday to Friday, 0=Sun.
 *
 * Weekends are left out because a coffee nobody is at work for is a
 * notification, not an introduction.
 */
export const COFFEE_DEFAULT_DAYS = [1, 2, 3, 4, 5];
/** When the draw happens, local to the workspace's own clock. */
export const COFFEE_DEFAULT_TIME = '10:00';
/**
 * How far back the draw looks to avoid repeating a pair.
 *
 * Four weeks is long enough that a twelve-person team works through most of
 * its combinations before anyone repeats, and short enough that a five-person
 * team is not left with nobody eligible.
 */
export const COFFEE_REPEAT_WEEKS = 4;
/**
 * How long somebody must have used the workspace to be worth pairing. Someone
 * who has not opened it in a fortnight is on leave, has left, or is a seat
 * nobody sits in — and a coffee invitation to any of those is a dead end for
 * whoever got matched with them.
 */
export const COFFEE_ELIGIBLE_DAYS = 14;
/**
 * How long a pair must share a room before it counts as having met.
 *
 * Shorter and two people crossing the lounge on their way elsewhere would be
 * recorded as a coffee; much longer and a genuinely quick chat would not.
 */
export const COFFEE_MEET_MINUTES = 3;
/* ---------------------------- files in a room --------------------------- */
/**
 * The shelves a new workspace's library starts with.
 *
 * A library with no shelves asks every first uploader to invent the filing
 * system, and what they invent is whatever the first document happened to be.
 * The industry already answered this question when the workspace was filed
 * under it — a clinic files protocols and consent forms, a chambers files
 * cases — so the shelves are put up before anybody arrives. They are ordinary
 * categories afterwards: rename them, recolour them, throw them out.
 */
export const INDUSTRY_FILE_SHELVES = {
    general: [
        { name: 'Documents', color: '#6366f1', description: 'Anything the team works from' },
        { name: 'Policies', color: '#0ea5e9', description: 'How this workplace does things' },
        { name: 'Contracts', color: '#f59e0b', description: 'Signed agreements' },
    ],
    technology: [
        { name: 'Specs', color: '#6366f1', description: 'What is being built, and why' },
        { name: 'Runbooks', color: '#22c55e', description: 'What to do when it breaks' },
        { name: 'Design', color: '#ec4899', description: 'Mocks, flows and assets' },
        { name: 'Contracts', color: '#f59e0b', description: 'Signed agreements' },
    ],
    education: [
        { name: 'Lesson plans', color: '#6366f1', description: 'What is taught, and when' },
        { name: 'Coursework', color: '#22c55e', description: 'Handed in, and handed back' },
        { name: 'Policies', color: '#0ea5e9', description: 'Safeguarding, conduct and procedure' },
        { name: 'Reports', color: '#f59e0b', description: 'Progress, attendance and inspection' },
    ],
    healthcare: [
        { name: 'Protocols', color: '#6366f1', description: 'How care is given here' },
        { name: 'Patient records', color: '#ef4444', description: 'Clinical notes and results' },
        { name: 'Consent forms', color: '#0ea5e9', description: 'What was agreed to, and by whom' },
        { name: 'Policies', color: '#f59e0b', description: 'Governance and procedure' },
    ],
    agency: [
        { name: 'Briefs', color: '#6366f1', description: 'What the client asked for' },
        { name: 'Creative', color: '#ec4899', description: 'Work in progress and finished cuts' },
        { name: 'Contracts', color: '#f59e0b', description: 'Statements of work and agreements' },
        { name: 'Invoices', color: '#22c55e', description: 'What was billed' },
    ],
    coworking: [
        { name: 'House rules', color: '#6366f1', description: 'How the floor works' },
        {
            name: 'Member agreements',
            color: '#f59e0b',
            description: 'Who has a desk, and on what terms',
        },
        { name: 'Invoices', color: '#22c55e', description: 'What was billed' },
    ],
    media: [
        { name: 'Scripts', color: '#6366f1', description: 'What is being made' },
        { name: 'Footage', color: '#ec4899', description: 'Rushes, stills and audio' },
        { name: 'Releases', color: '#0ea5e9', description: 'Permission to use what was shot' },
        { name: 'Contracts', color: '#f59e0b', description: 'Signed agreements' },
    ],
    wellness: [
        { name: 'Programmes', color: '#6366f1', description: 'Plans people are working through' },
        { name: 'Consent forms', color: '#0ea5e9', description: 'What was agreed to, and by whom' },
        { name: 'Policies', color: '#f59e0b', description: 'Safety and procedure' },
    ],
    support: [
        { name: 'Playbooks', color: '#6366f1', description: 'How each kind of case is handled' },
        { name: 'Escalations', color: '#ef4444', description: 'What went past the first line' },
        { name: 'Reports', color: '#22c55e', description: 'Volumes, times and outcomes' },
    ],
    events: [
        { name: 'Run sheets', color: '#6366f1', description: 'What happens, and at what time' },
        { name: 'Speakers', color: '#ec4899', description: 'Bios, decks and requirements' },
        { name: 'Floor plans', color: '#0ea5e9', description: 'Where everything goes' },
        { name: 'Contracts', color: '#f59e0b', description: 'Venue and supplier agreements' },
    ],
    legal: [
        { name: 'Case files', color: '#6366f1', description: 'The matter, and everything in it' },
        { name: 'Filings', color: '#0ea5e9', description: 'What went to the court' },
        { name: 'Precedents', color: '#22c55e', description: 'What has been argued before' },
        {
            name: 'Client agreements',
            color: '#f59e0b',
            description: 'Retainers and engagement terms',
        },
    ],
};
/**
 * Which of those shelves a document dropped in a room lands on when nobody
 * says otherwise. Always one of the industry's own shelves above.
 *
 * Never the sensitive one. A clinic's consult room files protocols by
 * default, not patient records: the shelf a one-tap upload lands on has to be
 * the one that is harmless to get wrong.
 */
export const INDUSTRY_ROOM_SHELF = {
    general: 'Documents',
    technology: 'Specs',
    education: 'Lesson plans',
    healthcare: 'Protocols',
    agency: 'Briefs',
    coworking: 'House rules',
    media: 'Scripts',
    wellness: 'Programmes',
    support: 'Playbooks',
    events: 'Run sheets',
    legal: 'Case files',
};
/**
 * Which rooms keep a drawer of their own.
 *
 * Not every room is somewhere documents belong. A meeting room is, everywhere
 * — that is what a table is for — and past that the industry decides: a
 * classroom holds the lesson, a studio stage holds the shoot, a focus pod
 * holds the spec somebody is heads-down on. A lounge holds nothing, and a
 * drawer nailed to the wall of one is a filing cabinet by the sofa.
 *
 * Listed as zone kinds rather than as a flag on the zone so the answer is the
 * same on every floor of a workspace without anyone maintaining it.
 */
export const INDUSTRY_FILE_ROOM_KINDS = {
    general: ['room'],
    technology: ['room', 'focus'],
    education: ['room', 'class'],
    healthcare: ['room'],
    agency: ['room', 'stage'],
    coworking: ['room', 'focus'],
    media: ['room', 'stage', 'broadcast'],
    wellness: ['room'],
    support: ['room', 'focus'],
    events: ['room', 'stage'],
    legal: ['room', 'focus'],
};
/**
 * Whether a room of this kind, in this industry, keeps files.
 *
 * Both halves have to hold: a walkway is not a room whatever the industry
 * says, and a room the industry does not file in has no drawer.
 */
export function roomKeepsFiles(kind, industry) {
    return isRoomZone(kind) && INDUSTRY_FILE_ROOM_KINDS[industry].includes(kind);
}
export const DEFAULT_TASK_COLUMNS = [
    { status: 'backlog', label: 'Backlog' },
    { status: 'todo', label: 'To do' },
    { status: 'in_progress', label: 'In progress' },
    { status: 'in_review', label: 'In review' },
    { status: 'done', label: 'Done' },
];
/* --------------------- per-assignee completion ------------------------- */
/**
 * Everything about a task that only holds for one person.
 *
 * A task carries a single `status`, so a board column is a fact about the
 * whole team. Tasks with `eachAssigneeCompletes` set break that: four people
 * each have their own share, the card stays where it is until the last tick,
 * and someone who finished a week ago still sees their card sitting in
 * "In progress" with nothing left for them to do. These three functions are
 * what let a board answer "where does this card belong *for me*" without each
 * client inventing its own rule.
 */
/** How many of the people currently assigned have ticked their own share. */
export function assigneesDone(task) {
    return task.assigneeCompletions.filter((completion) => task.assignees.some((assignee) => assignee.id === completion.userId)).length;
}
/** Whether this person has ticked their own share of a per-assignee task. */
export function hasFinishedOwnShare(task, userId) {
    if (!task.eachAssigneeCompletes || !userId)
        return false;
    return (task.assignees.some((assignee) => assignee.id === userId) &&
        task.assigneeCompletions.some((completion) => completion.userId === userId));
}
/**
 * The column this card belongs in for one viewer.
 *
 * Identical to `task.status` for everything else — only a per-assignee task
 * the viewer has personally finished moves, and only in the viewer's own
 * board. Nobody else's board changes, and the stored status does not either.
 */
export function personalTaskStatus(task, userId) {
    return hasFinishedOwnShare(task, userId) ? 'done' : task.status;
}
/**
 * The open tasks to offer as focus targets, most pressing first.
 *
 * One ordering, shared by the web picker, the phone picker and the default
 * both land on, because "the task the app thinks you should be doing" must
 * not be a different task depending on which screen you opened.
 *
 * Due dates lead, because a deadline is the only piece of urgency that is not
 * somebody's opinion. Priority breaks ties among the undated, and work
 * already in progress edges ahead of work that has not started — finishing
 * beats starting.
 */
export function focusCandidates(tasks) {
    const rank = (task) => [
        task.dueDate ? 0 : 1,
        task.dueDate ? new Date(task.dueDate).getTime() : 0,
        -TASK_PRIORITIES.indexOf(task.priority),
        task.status === 'in_progress' ? 0 : 1,
    ];
    return tasks
        .filter((task) => task.status !== 'done')
        .map((task) => ({ task, key: rank(task) }))
        .sort((a, b) => compareRanks(a.key, b.key) || a.task.title.localeCompare(b.task.title))
        .map((entry) => entry.task);
}
/** First difference wins, like comparing version numbers. */
function compareRanks(a, b) {
    for (let index = 0; index < a.length; index += 1) {
        if (a[index] !== b[index])
            return a[index] - b[index];
    }
    return 0;
}
/**
 * The name given to the private board every person gets on demand. Held here
 * so the server that mints it and the clients that recognise it agree — the
 * name is how "Personal" is told apart from a board someone happened to call
 * something similar.
 */
export const PERSONAL_PROJECT_NAME = 'Personal';
/** Lexicographic ordering step used for drag-and-drop board ordering. */
export const ORDER_STEP = 1000;
/* ----------------------------- workflows ------------------------------- */
export const TASK_RECURRENCES = ['none', 'daily', 'weekly', 'biweekly', 'monthly'];
/**
 * What a rule is written about.
 *
 * A rule reads either a task or a floor, never both: the triggers, the
 * conditions and the actions all differ, and a rule that tried to assign a
 * task to somebody because they walked into a room would be a rule with no
 * task to assign. The scope is what keeps those two vocabularies apart.
 */
export const AUTOMATION_SCOPES = ['project', 'space'];
/** Events on a task a rule can fire on. */
export const TASK_AUTOMATION_TRIGGERS = [
    'task_created',
    'task_status_changed',
    'task_assigned',
    'task_priority_changed',
    'task_labelled',
    'task_due_soon',
    'task_completed',
];
/** Events on a floor a rule can fire on. */
export const SPACE_AUTOMATION_TRIGGERS = [
    'zone_entered',
    'zone_left',
    'zone_became_empty',
    'zone_first_arrival',
    'space_first_arrival',
];
/** Events an automation rule can fire on. */
export const AUTOMATION_TRIGGERS = [
    ...TASK_AUTOMATION_TRIGGERS,
    ...SPACE_AUTOMATION_TRIGGERS,
];
export const AUTOMATION_ACTIONS = [
    'assign',
    'set_status',
    'set_priority',
    'add_label',
    'remove_label',
    'set_due_in_days',
    'add_checklist_item',
    'post_to_channel',
    'notify_users',
    'add_comment',
];
/** The task facts a condition can read. */
export const TASK_AUTOMATION_CONDITION_FIELDS = [
    'status',
    'priority',
    'label',
    'assignee',
    'title_contains',
    'has_due_date',
    'is_blocked',
];
/** The floor facts a condition can read. */
export const SPACE_AUTOMATION_CONDITION_FIELDS = ['zone_kind', 'zone_name', 'member_role'];
export const AUTOMATION_CONDITION_FIELDS = [
    ...TASK_AUTOMATION_CONDITION_FIELDS,
    ...SPACE_AUTOMATION_CONDITION_FIELDS,
];
export const AUTOMATION_OPERATORS = [
    'is',
    'is_not',
    'contains',
    'is_empty',
    'is_not_empty',
];
export const AUTOMATION_TRIGGER_INFO = {
    task_created: { label: 'Task is created', hint: 'Fires once, when a task first appears' },
    task_status_changed: { label: 'Status changes', hint: 'Fires on every column move' },
    task_assigned: { label: 'Someone is assigned', hint: 'Fires when assignees are added' },
    task_priority_changed: { label: 'Priority changes', hint: 'Fires when priority is edited' },
    task_labelled: { label: 'A label is added', hint: 'Fires when labels change' },
    task_due_soon: { label: 'Due date is near', hint: 'Checked hourly, 24h before the due date' },
    task_completed: { label: 'Task is completed', hint: 'Fires when a task reaches Done' },
    zone_entered: { label: 'Someone enters a room', hint: 'Fires as an avatar crosses into a zone' },
    zone_left: { label: 'Someone leaves a room', hint: 'Fires as an avatar walks out, or goes offline' },
    zone_became_empty: {
        label: 'A room empties',
        hint: 'Fires a few seconds after the last person leaves, so a walk-through does not count',
    },
    zone_first_arrival: {
        label: 'First arrival in a room',
        hint: "Fires once a day, for the first person into that room — the workspace's day",
    },
    space_first_arrival: {
        label: 'First arrival on a floor',
        hint: "Fires once a day, for the first person onto that floor — the workspace's day",
    },
};
export const AUTOMATION_ACTION_INFO = {
    assign: { label: 'Assign to', needs: 'user' },
    set_status: { label: 'Move to', needs: 'status' },
    set_priority: { label: 'Set priority to', needs: 'priority' },
    add_label: { label: 'Add label', needs: 'label' },
    remove_label: { label: 'Remove label', needs: 'label' },
    set_due_in_days: { label: 'Set due date in (days)', needs: 'number' },
    add_checklist_item: { label: 'Add checklist item', needs: 'text' },
    post_to_channel: { label: 'Post to channel', needs: 'channel' },
    notify_users: { label: 'Notify', needs: 'users' },
    add_comment: { label: 'Comment', needs: 'text' },
};
/** Actions that only make sense with a task in hand. */
export const TASK_AUTOMATION_ACTIONS = AUTOMATION_ACTIONS;
/**
 * Actions a rule about a floor may take.
 *
 * Deliberately short: a rule fired by somebody walking through a door has no
 * task to label and no checklist to add to, so the only things it can do are
 * things that involve people — say something, tell someone, or change the
 * status of whoever set it off.
 */
export const SPACE_AUTOMATION_ACTIONS = ['post_to_channel', 'notify_users', 'set_status'];
/**
 * What an action's value means on a floor, where it differs.
 *
 * `set_status` is the one word both vocabularies use: on a task it is the
 * column, on a floor it is the presence of the person who triggered the rule.
 */
export const SPACE_AUTOMATION_ACTION_NEEDS = {
    set_status: 'presence',
    post_to_channel: 'channel',
    notify_users: 'users',
};
/** Labels for every condition field, used by the rule builder. */
export const AUTOMATION_CONDITION_FIELD_INFO = {
    status: { label: 'Status', scope: 'project' },
    priority: { label: 'Priority', scope: 'project' },
    label: { label: 'Labels', scope: 'project' },
    assignee: { label: 'Assignees', scope: 'project' },
    title_contains: { label: 'Title', scope: 'project' },
    has_due_date: { label: 'Has a due date', scope: 'project' },
    is_blocked: { label: 'Is blocked', scope: 'project' },
    zone_kind: { label: 'Room type', scope: 'space' },
    zone_name: { label: 'Room name', scope: 'space' },
    member_role: { label: 'Their role', scope: 'space' },
};
/**
 * What a message posted by a floor rule may stand in for.
 *
 * Written out rather than left to a docs page: the builder prints this list
 * under the message box, which is the only place anybody will look for it.
 */
export const AUTOMATION_PLACEHOLDERS = [
    { token: '{{person}}', hint: 'who set the rule off' },
    { token: '{{zone}}', hint: 'the room it happened in' },
    { token: '{{space}}', hint: 'the floor it happened on' },
];
/** The scope a trigger belongs to. Triggers are unique across both. */
export function automationScopeOfTrigger(trigger) {
    return SPACE_AUTOMATION_TRIGGERS.includes(trigger) ? 'space' : 'project';
}
/** The actions a rule of this scope is allowed to take. */
export function automationActionsForScope(scope) {
    return scope === 'space' ? SPACE_AUTOMATION_ACTIONS : TASK_AUTOMATION_ACTIONS;
}
/** The triggers a rule of this scope is allowed to fire on. */
export function automationTriggersForScope(scope) {
    return scope === 'space' ? SPACE_AUTOMATION_TRIGGERS : TASK_AUTOMATION_TRIGGERS;
}
/**
 * Why this rule cannot be saved as written, or null when it can.
 *
 * One function rather than a rule in the zod schema and another in the route,
 * because a PATCH that changes only the actions still has to be judged against
 * the scope the rule already has — which zod cannot see.
 */
export function automationCompatibilityError(input) {
    const { scope, trigger, actions } = input;
    if (trigger && automationScopeOfTrigger(trigger) !== scope) {
        return `"${AUTOMATION_TRIGGER_INFO[trigger].label}" is not a ${scope === 'space' ? 'space' : 'project'} trigger`;
    }
    const allowed = automationActionsForScope(scope);
    const stray = actions?.find((action) => !allowed.includes(action.kind));
    if (stray) {
        return `"${AUTOMATION_ACTION_INFO[stray.kind].label}" cannot run on a ${scope} rule`;
    }
    return null;
}
/**
 * Automations can cascade (an action changes a task, which fires another
 * rule). This caps the chain so a pair of rules cannot loop forever.
 */
export const AUTOMATION_MAX_DEPTH = 3;
/* ------------------------------- widgets -------------------------------- */
/**
 * Where a widget can be placed. The same widget code renders on all four, so
 * a widget declares the surfaces it makes sense on rather than each surface
 * keeping its own list.
 *
 * `mobile` is the phone's home screen — those widgets are rendered natively
 * from a cached snapshot, so only the ones whose data fits in a small JSON
 * payload are offered there.
 */
/**
 * Where a widget can be pinned.
 *
 * `home` is the personal one, and it is deliberately one surface rather than
 * two: a section added at a desk is on the phone by return, because the older
 * split between a `dashboard` and a `mobile` board meant building the same
 * home twice and finding it half-built on whichever device you reached for
 * second. Both legacy names stay in the union so the migration that folds them
 * into `home` still has something to read.
 */
export const WIDGET_SURFACES = ['home', 'dashboard', 'channel', 'zone', 'mobile'];
/** The surfaces the old per-device split left behind, folded into `home`. */
export const LEGACY_PERSONAL_SURFACES = ['dashboard', 'mobile'];
export const WIDGET_KINDS = [
    'stats',
    'focus',
    'my_day',
    'my_tasks',
    'calendar',
    'projects',
    'spaces',
    'unread',
    'meetings',
    'who_is_here',
    'activity',
    'pinned',
    'countdown',
    'notes',
    'links',
    'standings',
    'clocks',
    'reminders',
    'team_workload',
    'team_attendance',
    'radio',
    /* The hero cards: one section each for the parts of the workspace that had
       no way onto home, drawn with an accent rather than the plain panel. */
    'engagement',
    'announcements',
    'stories',
    'who_is_off',
    'surveys',
    'celebrations',
    'coffee',
];
/**
 * How the section picker files a kind. Purely presentational — it keeps a
 * list of nineteen sections from reading as one undifferentiated wall.
 */
/**
 * The colour a hero section is drawn in.
 *
 * Named for what it is rather than for a hex value, because the two clients
 * hold different palettes: `flame` is the phone's orange tint and the
 * browser's amber ramp, and both read as the same section.
 */
export const WIDGET_ACCENTS = ['brand', 'flame', 'leaf', 'sky'];
export const WIDGET_GROUPS = [
    'Your day',
    'Your work',
    'The workspace',
    'Your team',
    'Personal',
];
export const WIDGET_INFO = {
    stats: {
        label: 'Today at a glance',
        hint: 'Open tasks, overdue work, who is in the office, meetings today',
        surfaces: ['home'],
        defaultSpan: 4,
        defaultRows: 1,
        group: 'Your day',
        suggested: true,
    },
    focus: {
        label: 'Needs you now',
        hint: 'Meetings starting within the quarter-hour and anything overdue',
        surfaces: ['home'],
        defaultSpan: 4,
        defaultRows: 1,
        group: 'Your day',
        suggested: true,
    },
    my_day: {
        label: 'My day',
        hint: 'Tasks due, meetings and mentions for today, in one list',
        surfaces: ['home', 'dashboard', 'mobile'],
        defaultSpan: 2,
        defaultRows: 3,
        group: 'Your day',
    },
    my_tasks: {
        label: 'My tasks',
        hint: 'Everything assigned to you, soonest due first',
        surfaces: ['home', 'dashboard', 'channel', 'mobile'],
        defaultSpan: 2,
        defaultRows: 3,
        group: 'Your work',
        feature: 'tasks',
        suggested: true,
    },
    calendar: {
        label: 'Calendar',
        hint: 'The week ahead — meetings and task due dates on one strip',
        surfaces: ['home'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your day',
        feature: 'meetings',
        suggested: true,
    },
    projects: {
        label: 'Projects',
        hint: 'Every project you can see, with how far along it is',
        surfaces: ['home', 'zone'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your work',
        feature: 'tasks',
        suggested: true,
    },
    spaces: {
        label: 'Spaces',
        hint: 'The rooms your team works in, and how busy each one is',
        surfaces: ['home'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'The workspace',
        feature: 'spaces',
        suggested: true,
    },
    unread: {
        label: 'Unread',
        hint: 'Unread and mention counts across your channels',
        surfaces: ['home', 'dashboard', 'mobile'],
        defaultSpan: 1,
        defaultRows: 2,
        group: 'Your work',
        feature: 'chat',
    },
    meetings: {
        label: 'Upcoming meetings',
        hint: 'The next few meetings, with a join button',
        surfaces: ['home', 'dashboard', 'zone', 'mobile'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your day',
        feature: 'meetings',
    },
    who_is_here: {
        label: 'Who is in the office',
        hint: 'People on the floor right now, and where',
        surfaces: ['home', 'dashboard', 'zone'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'The workspace',
        feature: 'spaces',
    },
    activity: {
        label: 'Recent activity',
        hint: 'Recent task and project changes',
        surfaces: ['home', 'dashboard', 'channel', 'zone'],
        defaultSpan: 2,
        defaultRows: 3,
        group: 'The workspace',
        suggested: true,
    },
    pinned: {
        label: 'Pinned',
        hint: 'The pinned messages of a channel',
        surfaces: ['channel', 'zone'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your work',
        feature: 'chat',
    },
    radio: {
        label: 'Radio',
        hint: 'Hold to talk to the people you reach most, without opening the conversation',
        surfaces: ['home'],
        defaultSpan: 1,
        defaultRows: 1,
        group: 'Personal',
        feature: 'chat',
        suggested: true,
    },
    countdown: {
        label: 'Countdown',
        hint: 'Time left until a date you set — a launch, a demo, a deadline',
        surfaces: ['home', 'dashboard', 'channel', 'zone', 'mobile'],
        defaultSpan: 1,
        defaultRows: 1,
        group: 'Personal',
    },
    notes: {
        label: 'Notes',
        hint: 'A shared scratchpad that everyone who can see the surface can edit',
        surfaces: ['home', 'dashboard', 'channel', 'zone'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Personal',
    },
    links: {
        label: 'Links',
        hint: 'The handful of links this team opens every day',
        surfaces: ['home', 'dashboard', 'channel', 'zone'],
        defaultSpan: 1,
        defaultRows: 2,
        group: 'Personal',
    },
    standings: {
        label: 'Game standings',
        hint: 'The board-game leaderboard',
        surfaces: ['home', 'dashboard', 'zone'],
        defaultSpan: 1,
        defaultRows: 2,
        group: 'The workspace',
        feature: 'games',
    },
    clocks: {
        label: 'World clocks',
        hint: 'Local time for the timezones your team spans',
        surfaces: ['home', 'dashboard', 'zone', 'mobile'],
        defaultSpan: 1,
        defaultRows: 1,
        group: 'Personal',
    },
    reminders: {
        label: 'Reminders',
        hint: 'Things you told yourself to do, personal and work, soonest first',
        surfaces: ['home', 'dashboard', 'mobile'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Personal',
    },
    team_workload: {
        label: 'Team workload',
        hint: 'Open and overdue tasks per person — for the people you manage',
        surfaces: ['home', 'dashboard'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your team',
        feature: 'tasks',
    },
    team_attendance: {
        label: 'Team attendance',
        hint: 'Who is online today and for how long — for the people you manage',
        surfaces: ['home', 'dashboard'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your team',
    },
    engagement: {
        label: 'Your streak',
        hint: 'The streak you are keeping, your Appys, and where you sit this week',
        surfaces: ['home'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your day',
        feature: 'engagement',
        accent: 'flame',
    },
    announcements: {
        label: 'Announcements',
        hint: 'What the company has said, and anything still waiting on your read',
        surfaces: ['home', 'zone'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'The workspace',
        feature: 'company',
        accent: 'sky',
    },
    stories: {
        label: 'Stories',
        hint: 'The last day of the workspace, from the people in it',
        surfaces: ['home'],
        defaultSpan: 4,
        defaultRows: 1,
        group: 'The workspace',
        feature: 'chat',
        accent: 'brand',
    },
    who_is_off: {
        label: "Who's off",
        hint: 'Approved leave — who is away today, and who is away next',
        surfaces: ['home'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your team',
        feature: 'company',
        accent: 'leaf',
    },
    surveys: {
        label: 'Pulse check',
        hint: 'Open surveys waiting on your answer — answered from the card',
        surfaces: ['home'],
        defaultSpan: 2,
        defaultRows: 1,
        group: 'Your team',
        feature: 'company',
        accent: 'brand',
    },
    celebrations: {
        label: 'Coming up',
        hint: 'Birthdays and work anniversaries over the next month',
        surfaces: ['home', 'channel'],
        defaultSpan: 2,
        defaultRows: 2,
        group: 'Your team',
        feature: 'company',
    },
    coffee: {
        label: 'Coffee chat',
        hint: "Today's random 1:1, with a one-tap walk to the lounge table",
        surfaces: ['home', 'mobile'],
        defaultSpan: 2,
        defaultRows: 1,
        group: 'Your team',
        feature: 'engagement',
        accent: 'flame',
    },
};
/**
 * Kinds worth having more than one of.
 *
 * They are exactly the kinds that carry their own settings: two countdowns
 * count down to two different dates, but two "My tasks" are the same list
 * twice. The picker greys out the rest once they are on the board.
 */
export const REPEATABLE_WIDGET_KINDS = [
    'countdown',
    'notes',
    'links',
    'clocks',
    'pinned',
];
/**
 * The home everyone had before it became something you build yourself.
 *
 * A new account is seeded with exactly this, so nobody's first login is a
 * blank page, and "Reset" puts it back. The order is the phone's reading
 * order — your day, then your work, then the workspace around it.
 */
export const HOME_TEMPLATE = [
    { kind: 'stats', span: 4, rows: 1 },
    { kind: 'focus', span: 4, rows: 1 },
    { kind: 'my_tasks', span: 2, rows: 3 },
    { kind: 'calendar', span: 2, rows: 2 },
    { kind: 'spaces', span: 2, rows: 2 },
    { kind: 'projects', span: 2, rows: 2 },
    { kind: 'activity', span: 2, rows: 3 },
];
/* ----------------------------- Widget sizing ----------------------------- */
/**
 * The height of one row unit, and the gap between two of them, in rem.
 *
 * Kept here rather than in either client so the phone and the browser agree
 * on what "two rows tall" means — a size set at a desk has to arrive on the
 * train as the same size, and a card that is two units on one and three on
 * the other is not one board.
 */
export const WIDGET_ROW_UNIT_REM = 11;
export const WIDGET_ROW_GAP_REM = 1;
/** How tall a card of `rows` units stands, in rem, gaps included. */
export function widgetHeightRem(rows) {
    return rows * WIDGET_ROW_UNIT_REM + (rows - 1) * WIDGET_ROW_GAP_REM;
}
/**
 * How many list rows a card of `rows` units has room for.
 *
 * Height that does not change what is drawn inside it is decoration: a card
 * dragged twice as tall that still shows six items has bought nothing but
 * whitespace. So a row unit is worth roughly four list rows, and every list
 * in a widget body scales off this rather than off a number typed into it.
 */
export function capacityFor(rows) {
    return rows * 4 - 1;
}
/**
 * What a body's own `base` count becomes in a card of this height.
 *
 * Bodies keep declaring the length they were written around — six recent
 * items, twelve tasks — and this scales that against the height someone has
 * actually chosen, taking the two-row card as the one those numbers were
 * written for. A widget on a surface with no height of its own is left
 * exactly as it was.
 */
export function scaleCapacity(base, rows = 2) {
    return Math.max(1, Math.round((base * capacityFor(rows)) / capacityFor(2)));
}
/** A surface holding more than this stops being a dashboard and becomes a page. */
export const MAX_WIDGETS_PER_SURFACE = 12;
/**
 * Home is the one surface people really do fill, and the template alone is
 * already seven sections — a twelve-widget ceiling would leave room for five
 * more before the "Add a section" button went quiet on a fresh account.
 */
export const MAX_HOME_SECTIONS = 24;
export function maxWidgetsFor(surface) {
    return surface === 'home' ? MAX_HOME_SECTIONS : MAX_WIDGETS_PER_SURFACE;
}
/* ---------------------------- wall screens ------------------------------ */
/**
 * The widgets a screen on the office wall may show.
 *
 * A wall screen is read over someone's shoulder from three metres away by
 * whoever happens to be standing in the room, so the only sections that
 * belong on one are the ones that mean the same thing to everybody who can
 * see them. That rules out every personal section by construction — `my_day`,
 * `my_tasks`, `unread`, `reminders`, `focus`, `stats` and `engagement` all
 * answer "what is on *your* plate", and a wall answering that is either
 * meaningless to the room or an accidental broadcast of one person's
 * workload. `team_workload` and `team_attendance` are worse again: they name
 * individuals and rank them, which is a manager's view and not a poster.
 *
 * What is left is the ambient stuff a team genuinely wants on a wall — who is
 * on the floor, what is on next, how long until the launch, what changed, and
 * the notes and links the room keeps by the door.
 *
 * Derived from `WIDGET_INFO` rather than hand-listed so the two can never
 * disagree: a kind is offered on a screen exactly when it declares the `zone`
 * surface it would already be shown on inside a room's panel.
 */
export const SCREEN_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => WIDGET_INFO[kind].surfaces.includes('zone'));
export function isScreenWidgetKind(kind) {
    return SCREEN_WIDGET_KINDS.includes(kind);
}
/**
 * How much text fits on the face of a prop four tiles wide.
 *
 * The miniature drawn on the floor is not the widget — it is the glance that
 * tells you whether walking over is worth it — so it carries a heading and
 * this many lines, and anything longer is cut rather than shrunk to a size
 * nobody can read from across the room.
 */
export const SCREEN_SNAPSHOT_MAX_LINES = 4;
/** Beyond this a line is ellipsised; the face is only ~128px wide. */
export const SCREEN_SNAPSHOT_MAX_CHARS = 34;
/** How often the floor asks the server what its screens are showing. */
export const SCREEN_SNAPSHOT_REFRESH_MS = 45_000;
export const AVATAR_COLORS = [
    '#f97316',
    '#ef4444',
    '#ec4899',
    '#a855f7',
    '#6366f1',
    '#3b82f6',
    '#06b6d4',
    '#14b8a6',
    '#22c55e',
    '#eab308',
];
/* ------------------------- walkie talkie -------------------------------- */
/**
 * How long one transmitted segment runs.
 *
 * Live audio is sent as a run of small self-contained clips rather than a
 * media stream: both clients can already record and decode AAC, and neither
 * needs an SFU to do it. The length is the trade — longer clips waste less on
 * container overhead and record gaps, shorter ones reach the far end sooner.
 * At a second and a bit, someone holding the button is heard about as fast as
 * a phone call rings.
 */
export const WALKIE_SEGMENT_MS = 1200;
/** A segment past this is not speech, it is someone's upload being tested. */
export const WALKIE_MAX_CHUNK_BYTES = 512 * 1024;
/**
 * A held button eventually has to let go on its own — a pocketed phone with
 * the finger still down would otherwise transmit the room until it died.
 */
export const WALKIE_MAX_TRANSMISSION_MS = 60_000;
/**
 * How long one listener stays quiet after a radio push.
 *
 * A radio conversation is a run of short presses, and the person the push is
 * for has their phone in a pocket: the first press is the one that tells them
 * someone wants them, and the next six are the same fact arriving six more
 * times. Long enough to cover a back-and-forth, short enough that a call an
 * hour later still gets through.
 */
export const WALKIE_OFFLINE_PUSH_COOLDOWN_MS = 5 * 60_000;
/**
 * The platforms someone can ask to be told about.
 *
 * Only Android is open. iOS is on the App Store, so a waitlist for it would be
 * a form that collects an address in exchange for nothing; the web app needs
 * no list at all. If a third platform is ever announced before it ships, it
 * joins this list rather than becoming a second endpoint.
 */
export const WAITLIST_PLATFORMS = ['android'];
/* ------------------------------ webhooks -------------------------------- */
/**
 * What an outgoing webhook can subscribe to. Each one is a fact the workspace
 * already broadcasts to its own clients over a socket; a webhook is the same
 * fact handed to something outside. `message.created` is channel messages
 * only — a DM is a private conversation and never leaves the workspace.
 */
export const OUTGOING_WEBHOOK_EVENTS = [
    'message.created',
    'task.created',
    'task.updated',
    'task.completed',
    'meeting.scheduled',
    'member.joined',
    'kudos.given',
];
export const OUTGOING_WEBHOOK_EVENT_INFO = {
    'message.created': {
        label: 'Message posted',
        hint: 'A message lands in a channel. Direct messages are never sent.',
    },
    'task.created': { label: 'Task created', hint: 'A new task is added to a project.' },
    'task.updated': { label: 'Task updated', hint: 'A task changes — status, assignee, priority, labels.' },
    'task.completed': { label: 'Task completed', hint: 'A task is moved to done.' },
    'meeting.scheduled': { label: 'Meeting scheduled', hint: 'A meeting is put on the calendar.' },
    'member.joined': { label: 'Member joined', hint: 'Someone becomes a member of the workspace.' },
    'kudos.given': { label: 'Appy given', hint: 'Someone sends a colleague an Appy.' },
};
/** The event "Send test" posts. Not subscribable: every hook gets it on request. */
export const OUTGOING_WEBHOOK_TEST_EVENT = 'ping';
/** Consecutive failed deliveries before a hook is switched off on its own. */
export const OUTGOING_WEBHOOK_MAX_FAILURES = 20;
export const MAX_WEBHOOK_NAME_LENGTH = 80;
/** A hook per integration is the norm; the cap is there to stop a runaway script. */
export const MAX_WEBHOOKS_PER_WORKSPACE = 50;
/** Incoming posts share the message limit; a flood of attachments is cut, not refused. */
export const MAX_INCOMING_WEBHOOK_ATTACHMENTS = 10;
